import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm, Controller, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader } from "@/components/shared/loader";
import { Pages } from "@/constants/enums";
import { appPath } from "@/constants/paths";
import FormFields from "@/components/shared/form-fields/form-fields";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Form } from "@/components/ui/form";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { useRoles } from "@/features/roles/hooks/useRolesQueries";
import {
  AssignRoleDtoRole,
  CreateUserDtoRole,
  UserResponseDtoRole,
} from "@/types/api";
import type { User, UserRole } from "@/types/user";
import { createUserValidation, updateUserValidation } from "@/validations/user";
import {
  useAssignRole,
  useCreateUser,
  useUpdateUser,
} from "@/features/users/hooks/useUsersMutations";
import { useDepartments } from "@/features/departments/hooks/useDepartmentsQueries";
import { isSuperAdmin } from "@/lib/auth-utils";
import { useUserPermissions } from "@/hooks/useUserPermissions";
import { useUser } from "@/features/auth/store";
import {
  actorMayManageUser,
  filterRolesAssignableByActor,
} from "@/lib/role-management-policy";
import { useGovernanceAllowedDepartments } from "@/hooks/useDepartmentScope";
import useFormFields from "@/hooks/useFormFields";
import ReactSelect from "react-select";
import { CredentialsDialog } from "@/features/users/components/CredentialsDialog";
interface UserFormPageProps {
  user?: User;
}

type UserFormValues = {
  name: string;
  username?: string;
  password?: string;
  email: string;
  role?: string;
  departmentId?: string;
  subAdminDepartmentIds?: string[];
  isActive?: boolean;
};

function toCreateRole(role: string): CreateUserDtoRole {
  return role as CreateUserDtoRole;
}

function toAssignRole(role: UserRole): AssignRoleDtoRole {
  return role as AssignRoleDtoRole;
}

function splitName(name: string): { firstName: string; lastName?: string } {
  const trimmedName = name.trim();
  const nameParts = trimmedName.split(/\s+/);
  const firstName = nameParts[0] || "";
  const lastName = nameParts.slice(1).join(" ") || undefined;
  return { firstName, lastName };
}

export function UserFormPage({ user }: UserFormPageProps) {
  const navigate = useNavigate();
  const sessionUser = useUser();
  const { canAssignRole } = useUserPermissions();
  const mayManageTarget = !user || actorMayManageUser(sessionUser, user);
  const { getFormFields } = useFormFields({ slug: Pages.USERS });
  const { data: departmentsData } = useDepartments({ limit: 100 });
  const { data: roles = [] } = useRoles();
  const createMutation = useCreateUser();
  const updateMutation = useUpdateUser();
  const assignRoleMutation = useAssignRole();

  const [credentials, setCredentials] = useState<{
    username: string;
    temporaryPassword: string;
    email: string;
  } | null>(null);

  const departments = useGovernanceAllowedDepartments(
    useMemo(() => departmentsData?.items ?? [], [departmentsData?.items]),
  );
  const departmentOptions = useMemo(
    () => departments.map((d) => ({ value: d.id, label: d.nameAr })),
    [departments],
  );

  // Ownership (which department an account belongs to) stays with Super Admin once it exists.
  const departmentLocked = Boolean(user) && !isSuperAdmin(sessionUser);

  // Super Admin picks any role (Editor preselected); a Sub-Admin's accounts are always Editors, so
  // the menu shows that single fixed option even when the roles list is not readable for them.
  const roleOptions = useMemo(() => {
    if (canAssignRole) {
      return filterRolesAssignableByActor(sessionUser, roles).map((r) => ({
        value: r.role,
        label: r.name,
      }));
    }
    const editor = roles.find((r) => r.role === UserResponseDtoRole.EDITOR);
    return [{ value: UserResponseDtoRole.EDITOR, label: editor?.name ?? "محرر" }];
  }, [canAssignRole, roles, sessionUser]);

  const formMethods = useForm<UserFormValues>({
    defaultValues: {
      name: user
        ? `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim()
        : "",
      email: user?.email ?? "",
      username: user?.username ?? "",
      password: "",
      role: user?.role ?? UserResponseDtoRole.EDITOR,
      departmentId: user?.departmentId ?? undefined,
      subAdminDepartmentIds: user?.subAdminDepartmentIds ?? [],
      isActive: user?.isActive ?? true,
    },
    mode: "onChange",
    resolver: zodResolver(user ? updateUserValidation : createUserValidation),
  });

  const {
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
  } = formMethods;

  const selectedRole = useWatch({ control, name: "role" });
  const showSubAdminDepartments =
    canAssignRole && selectedRole === UserResponseDtoRole.SUB_ADMIN;

  const isPending =
    isSubmitting ||
    createMutation.isPending ||
    updateMutation.isPending ||
    assignRoleMutation.isPending;

  const baseFields = getFormFields()
    .filter((field) => field.name !== "role")
    .map((field) => {
      if (field.name === "password") {
        return {
          ...field,
          placeholder: user
            ? "اتركها فارغة للإبقاء على كلمة المرور الحالية"
            : field.placeholder,
          description: user
            ? "أدخل كلمة مرور جديدة أو استخدم «توليد تلقائي»"
            : "اترك الحقل فارغاً لتوليد كلمة مرور تلقائياً عند الإنشاء",
        };
      }
      if (field.name === "username") {
        return {
          ...field,
          description: user
            ? "عدّل اسم المستخدم أو استخدم «توليد من الاسم»"
            : "أدخل اسم مستخدم أو استخدم «توليد من الاسم»، أو اتركه فارغاً للتوليد التلقائي عند الإنشاء",
        };
      }
      return field;
    });

  async function saveUser(data: UserFormValues) {
    const { firstName, lastName } = splitName(data.name);
    const email = data.email.trim();
    const username = data.username?.trim() ?? "";
    const password = data.password?.trim() ?? "";
    const role = data.role ?? UserResponseDtoRole.EDITOR;
    const departmentId = data.departmentId?.trim() || undefined;
    const subAdminDepartmentIds = showSubAdminDepartments
      ? (data.subAdminDepartmentIds ?? [])
      : undefined;

    if (user) {
      const roleChanging = canAssignRole && role !== user.role;
      const becomingSubAdmin =
        roleChanging && role === UserResponseDtoRole.SUB_ADMIN;

      if (becomingSubAdmin) {
        await assignRoleMutation.mutateAsync({
          id: user.id,
          data: { role: toAssignRole(role) },
          silentSuccess: true,
        });
      }

      await updateMutation.mutateAsync({
        id: user.id,
        data: {
          email,
          username,
          firstName,
          lastName,
          departmentId,
          isActive: data.isActive,
          ...(password ? { password } : {}),
          ...(subAdminDepartmentIds !== undefined && { subAdminDepartmentIds }),
        },
        silentSuccess: roleChanging && !becomingSubAdmin,
      });

      if (roleChanging && !becomingSubAdmin) {
        await assignRoleMutation.mutateAsync({
          id: user.id,
          data: { role: toAssignRole(role) },
        });
      }
    } else {
      const result = await createMutation.mutateAsync({
        email,
        firstName,
        lastName,
        role: toCreateRole(data.role ?? UserResponseDtoRole.EDITOR),
        departmentId,
        ...(username ? { username } : {}),
        ...(password ? { password } : {}),
        ...(subAdminDepartmentIds !== undefined && { subAdminDepartmentIds }),
      });

      const { temporaryPassword, username: generatedUsername, email: userEmail } =
        result.data;
      if (temporaryPassword && generatedUsername) {
        setCredentials({
          username: generatedUsername,
          temporaryPassword,
          email: userEmail,
        });
        return;
      }

      navigate(appPath(Pages.USERS));
    }
  }

  const handleCredentialsDone = () => {
    setCredentials(null);
    navigate(appPath(Pages.USERS));
  };

  if (user && !mayManageTarget) {
    return (
      <p className="text-muted-foreground p-6">
        لا يمكنك تعديل هذا الحساب — يمكنك إدارة حسابات المحررين فقط.
      </p>
    );
  }

  return (
    <>
      <CredentialsDialog
        open={credentials !== null}
        onOpenChange={(open) => {
          if (!open) handleCredentialsDone();
        }}
        username={credentials?.username ?? ""}
        temporaryPassword={credentials?.temporaryPassword ?? ""}
        email={credentials?.email ?? ""}
        onDone={handleCredentialsDone}
      />
    <Form {...formMethods}>
      <form className="mx-auto max-w-2xl space-y-6">
        <Card>
          <CardContent className="space-y-6 p-6">
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-foreground">
                المعلومات الأساسية
              </h3>
              {baseFields.map((field, index) => (
                <FormFields
                  key={index}
                  {...field}
                  control={control}
                  errors={errors}
                />
              ))}
            </div>

            <Separator />

            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-foreground">
                الدور والدائرة
              </h3>

              <div className="space-y-2">
                <Label className="text-sm font-medium">الدائرة</Label>
                <Controller
                  name="departmentId"
                  control={control}
                  render={({ field }) => (
                    <Select
                      value={field.value ?? ""}
                      onValueChange={field.onChange}
                      disabled={departmentLocked}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="اختر الدائرة" />
                      </SelectTrigger>
                      <SelectContent>
                        {departments.map((dept) => (
                          <SelectItem key={dept.id} value={dept.id}>
                            {dept.nameAr}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
                {departmentLocked && (
                  <p className="text-xs text-muted-foreground">
                    نقل الحساب إلى دائرة أخرى من صلاحيات Super Admin فقط.
                  </p>
                )}
                {errors.departmentId && (
                  <p className="text-sm text-destructive">
                    {errors.departmentId.message}
                  </p>
                )}
              </div>

              {showSubAdminDepartments && (
                <div className="space-y-2">
                  <Label className="text-sm font-medium">
                    الدوائر المُدارة (للتذاكر والحوكمة)
                  </Label>
                  <Controller
                    name="subAdminDepartmentIds"
                    control={control}
                    render={({ field }) => {
                      const value = field.value ?? [];
                      const selected = departmentOptions.filter((opt) =>
                        value.includes(opt.value),
                      );
                      return (
                        <ReactSelect
                          isMulti
                          isRtl
                          options={departmentOptions}
                          value={selected}
                          onChange={(newValue) =>
                            field.onChange(
                              newValue
                                ? newValue.map((opt) => opt.value)
                                : [],
                            )
                          }
                          placeholder="اختر الدوائر التي يديرها هذا المستخدم"
                          className="text-sm"
                        />
                      );
                    }}
                  />
                  <p className="text-xs text-muted-foreground">
                    يحدد نطاق إدارة التذاكر والصلاحيات الإدارية لهذا المدير الفرعي.
                  </p>
                </div>
              )}

              <div className="space-y-2">
                <Label className="text-sm font-medium">نوع الحساب</Label>
                <Controller
                  name="role"
                  control={control}
                  render={({ field }) => (
                    <Select
                      value={field.value ?? UserResponseDtoRole.EDITOR}
                      onValueChange={field.onChange}
                      disabled={!canAssignRole}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="اختر نوع الحساب" />
                      </SelectTrigger>
                      <SelectContent>
                        {roleOptions.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
                {!canAssignRole && (
                  <p className="text-xs text-muted-foreground">
                    الحسابات التي تنشئها تكون بدور «محرر» دائماً؛ تغيير نوع الحساب من صلاحيات Super Admin.
                  </p>
                )}
              </div>

              {user && (
                <div className="flex items-center justify-between rounded-lg border border-border p-3">
                  <Label htmlFor="isActive" className="text-sm font-medium">
                    الحساب نشط
                  </Label>
                  <Controller
                    name="isActive"
                    control={control}
                    render={({ field }) => (
                      <Switch
                        id="isActive"
                        checked={field.value ?? true}
                        onCheckedChange={field.onChange}
                      />
                    )}
                  />
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <div className={cn("flex flex-col gap-3 sm:flex-row")}>
          <Button
            type="button"
            disabled={isPending}
            className="flex-1"
            onClick={handleSubmit(saveUser)}
          >
            {user ? "تحديث المستخدم" : "إنشاء المستخدم"}
            {isPending && <Loader className="mr-2" />}
          </Button>
          <Button
            type="button"
            variant="ghost"
            disabled={isPending}
            className="flex-1 sm:flex-none"
            onClick={() => navigate(appPath(Pages.USERS))}
          >
            إلغاء
          </Button>
        </div>
      </form>
    </Form>
    </>
  );
}
